import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/20/solid';
import { Candidate, Role, CandidateStatus } from '../../Models';
import useApiToken from '../../hooks/useApiToken';
import { useParams } from 'react-router-dom';
import ScoreGrades from '../Candidates/ScoreGrades';
import CandidateNotes from '../Candidates/CandidateNotes';
import {
  CandidateCvFlagResult,
  GetCandidate,
  GetCandidateCv,
  GetCandidateCvFlagResult,
  GenerateCandidateCvFlagResult,
} from '../../api/candidates.api';

interface CVViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidates: Candidate[];
  initialCandidateIndex: number;
  role: Role;
  onGradeUpdate?: (
    candidatesUpdate: CandidateStatus[],
    gradeStatusUpdate?: boolean,
    manualEvaluation?: { roleStatusId: string; rating: string; score?: number }
  ) => void;
}

const CVViewerModal: React.FC<CVViewerModalProps> = ({
  isOpen,
  onClose,
  candidates,
  initialCandidateIndex,
  role,
  onGradeUpdate
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialCandidateIndex);
  const [currentCandidate, setCurrentCandidate] = useState<Candidate | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [cvBlobUrl, setCvBlobUrl] = useState<string | null>(null);
  const [cvFlagResult, setCvFlagResult] = useState<CandidateCvFlagResult | null>(null);
  const [isFetchingCvFlags, setIsFetchingCvFlags] = useState(false);
  const [cvFlagError, setCvFlagError] = useState<string | null>(null);
  const token = useApiToken();
  const { roleId } = useParams();

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && candidates.length > 0) {
      setCurrentIndex(initialCandidateIndex);
    }
  }, [isOpen, initialCandidateIndex, candidates.length]);

  useEffect(() => {
    
    if (token && isOpen && candidates[currentIndex]) {
      loadCandidateData(candidates[currentIndex]);
    }
  }, [token,currentIndex, isOpen, candidates]);

  // Reset flags when switching candidates and auto-fetch stored result
  useEffect(() => {
    setCvFlagResult(null);
    setCvFlagError(null);

    const candidateId = candidates[currentIndex]?.id;
    if (!candidateId || !roleId || !token || !isOpen) return;

    let cancelled = false;
    setIsFetchingCvFlags(true);

    GetCandidateCvFlagResult(roleId, candidateId, token)
      .then((result) => {
        if (!cancelled) setCvFlagResult(result ?? null);
      })
      .catch((err: any) => {
        if (!cancelled) setCvFlagError(err?.message || 'Failed to fetch CV flags');
      })
      .finally(() => {
        if (!cancelled) setIsFetchingCvFlags(false);
      });

    return () => { cancelled = true; };
  }, [currentIndex, isOpen, token, roleId]);

  const handleGetCvFlags = async () => {
    const candidateId = candidates[currentIndex]?.id;
    if (!candidateId || !roleId || !token) return;

    setIsFetchingCvFlags(true);
    setCvFlagError(null);
    try {
      const result = await GenerateCandidateCvFlagResult(roleId, candidateId, token);
      setCvFlagResult(result);
    } catch (err: any) {
      setCvFlagError(err?.message || 'Failed to fetch CV flags');
    } finally {
      setIsFetchingCvFlags(false);
    }
  };

  const loadCandidateData = async (candidate: Candidate) => {
    if (!token || !roleId) return;

    setIsLoading(true);
    if (cvBlobUrl) {
      URL.revokeObjectURL(cvBlobUrl);
      setCvBlobUrl(null);
    }
    try {
      const [fullCandidate, cvBlob] = await Promise.all([
        GetCandidate(candidate.id, token),
        GetCandidateCv(candidate.id, token).catch(() => null),
      ]);
      setCurrentCandidate(fullCandidate);
      if (cvBlob) setCvBlobUrl(URL.createObjectURL(cvBlob));
    } catch (error) {
      console.error('Error loading candidate data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < candidates.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      else if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, candidates.length]);

  

if (!isOpen) return null;

  const cvUrl = cvBlobUrl ?? '';

  return createPortal(
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        <div className="relative w-full max-w-8xl h-[95vh] flex flex-col bg-white dark:bg-darkbglight rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-300">
                {currentCandidate ? `${currentCandidate.firstName} ${currentCandidate.lastName}` : 'CV Viewer'}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={goToPrevious}
                  disabled={currentIndex === 0}
                  className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {currentIndex + 1} of {candidates.length}
                </span>
                <button
                  onClick={goToNext}
                  disabled={currentIndex === candidates.length - 1}
                  className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentCandidate && (
                <button
                  onClick={() => window.open(`/roles/${roleId}/candidates/${currentCandidate.id}`, '_blank')}
                  className="px-3 py-1 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
                >
                  View Profile
                </button>
              )}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-1 min-h-0">
            {/* Left Sidebar - Candidate Details */}
            <div className="w-80 border-r border-gray-200 dark:border-gray-800 overflow-y-auto">
              <div className="p-4 space-y-6">
                {/* Basic Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">Basic Information</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-400">Email:</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-300">{currentCandidate?.email || '-'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-400">Location:</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-300">
                        {currentCandidate?.workLocation1City?.name}
                        {currentCandidate?.workLocation2City && `, ${currentCandidate.workLocation2City.name}`}
                      </span>
                    </div>
                    
                  </div>
                </div>

                {/* Education */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">Education</h4>
                  <div className="space-y-2">
                    {currentCandidate?.education?.slice(0, 3).map((edu, index) => (
                      <div key={index} className="text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        <div className="font-medium text-gray-900 dark:text-gray-300">{edu.university?.name}</div>
                        <div className="text-gray-600 dark:text-gray-400">{edu.postGrad ? 'PG' : 'UG'}</div>
                        <div className="text-gray-500 dark:text-gray-500 text-xs">{edu.period}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Experience */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">Experience</h4>
                  <div className="space-y-2">
                    {currentCandidate?.experience?.slice(0, 3).map((exp, index) => (
                      <div key={index} className="text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded">
                        <div className="font-medium text-gray-900 dark:text-gray-300">{exp.company?.name}</div>
                        <div className="text-gray-600 dark:text-gray-400">{exp.title}</div>
                        <div className="text-gray-500 dark:text-gray-500 text-xs">{exp.period}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CV Flags */}
                <div className="rounded-md border border-gray-200 dark:border-gray-600 p-3">
                  <div className="flex items-center justify-between gap-3">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-300">CV Flags</h4>
                    <button
                      type="button"
                      onClick={handleGetCvFlags}
                      disabled={isFetchingCvFlags}
                      className="inline-flex items-center gap-1 rounded-md bg-entntblue px-2.5 py-1.5 text-xs font-medium text-white hover:bg-entntorange disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <StarIcon className="h-4 w-4" />
                      {isFetchingCvFlags ? 'Fetching...' : 'Flag CV'}
                    </button>
                  </div>

                  {cvFlagError && (
                    <p className="mt-2 text-xs text-red-500">{cvFlagError}</p>
                  )}

                  {cvFlagResult && (
                    <div className="mt-3 space-y-2 text-xs text-gray-700 dark:text-gray-300">
                      <div className="flex items-center gap-3">
                        <span className="rounded bg-gray-100 px-2 py-1 dark:bg-gray-700">
                          Tier: {cvFlagResult.tier || 'N/A'}
                        </span>
                        <span className="rounded bg-gray-100 px-2 py-1 dark:bg-gray-700">
                          Score: {cvFlagResult.score ?? 'N/A'}
                        </span>
                      </div>

                      {cvFlagResult.summary && (
                        <p className="text-gray-600 dark:text-gray-400">{cvFlagResult.summary}</p>
                      )}

                      {cvFlagResult.flags?.length ? (
                        <ul className="space-y-1">
                          {cvFlagResult.flags.map((flag, idx) => (
                            <li
                              key={`${flag.flag_title}-${idx}`}
                              className="rounded border border-gray-200 dark:border-gray-600 p-2"
                            >
                              <p className="font-medium text-gray-800 dark:text-gray-200">{flag.flag_title}</p>
                              <p className="text-gray-600 dark:text-gray-400">{flag.reason}</p>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-500 dark:text-gray-400">No flags found.</p>
                      )}
                    </div>
                  )}

                  {!cvFlagResult && !isFetchingCvFlags && !cvFlagError && (
                    <p className="mt-2 text-xs text-gray-400">Click &quot;Flag CV&quot; to evaluate this candidate.</p>
                  )}
                </div>
                <div className="space-y-6">
                {currentCandidate && roleId && (
                  <ScoreGrades
                    candidateId={currentCandidate.id}
                    roleId={roleId}
                    hideScores={false}
                    candidate={currentCandidate}
                    onGradeUpdate={onGradeUpdate}
                  />
                )}
              </div>

                
              </div>
            </div>

            {/* Center - PDF Viewer */}
            <div className="flex-1 flex flex-col min-h-0">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-500 dark:text-gray-400">Loading CV...</div>
                </div>
              ) : cvUrl ? (
                <div className="h-full">
                  <iframe
                    src={cvUrl}
                    className="w-full h-full border-0"
                    title={`CV - ${currentCandidate ? `${currentCandidate.firstName} ${currentCandidate.lastName}` : ''}`}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-gray-500 dark:text-gray-400">No CV available</div>
                </div>
              )}
            </div>

            {/* Right Sidebar - Scores */}
            <div className="w-96 border-l border-gray-200 dark:border-gray-800 overflow-y-auto">
              <div className="p-4 space-y-6">
              {currentCandidate && roleId && (
                <CandidateNotes candidateId={currentCandidate.id} roleId={roleId} />
              )}
              </div>
              <div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default CVViewerModal;
